// HQ audiences. Rules here are only gender and whether the customer has a phone. Preview counts without saving.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { EmptyState, Loader, Modal, Spinner, WelcomeBar } from '../components/ui';

function rulesOf(gender, hasPhone) {
  // The audience API treats an empty gender as "any".
  return { gender: gender === 'any' ? '' : gender, has_phone: hasPhone };
}

function SegmentForm({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('any');
  const [hasPhone, setHasPhone] = useState(true);
  const [previewCount, setPreviewCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await api.previewSegment(rulesOf(gender, hasPhone));
      const count = typeof res === 'number' ? res : res?.count ?? res?.last_count;
      setPreviewCount(count ?? 0);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.createSegment({ name: name.trim(), rules: rulesOf(gender, hasPhone) });
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New segment" onClose={onClose}>
      <div className="de-form-grid">
        <label className="de-field">
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={name} onChange={(e) => setName(e.target.value)} placeholder="Women with a phone" />
        </label>
        <label className="de-field">
          <span className="de-label">Gender</span>
          <select
            className="de-select"
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              setPreviewCount(null);
            }}
          >
            <option value="any">Any</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
        </label>
        <label className="de-row" style={{ gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={hasPhone}
            onChange={(e) => {
              setHasPhone(e.target.checked);
              setPreviewCount(null);
            }}
          />
          <span className="de-small">Has phone</span>
        </label>
      </div>
      {previewCount != null ? <div className="de-callout de-mt-16">{previewCount} customers</div> : null}
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" disabled={previewing} onClick={preview}>
          {previewing ? <Spinner /> : null}
          Preview
        </button>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={busy || !name.trim()} onClick={save}>
          {busy ? <Spinner light /> : null}
          Save
        </button>
      </div>
    </Modal>
  );
}

export default function Segments() {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () =>
    api
      .listSegments()
      .then(setRows)
      .catch((e) => {
        toast.error(e.message);
        setRows((current) => current ?? []);
      });

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="de-fade">
      <WelcomeBar title="Segments" sub="Saved audiences for campaigns. Preview counts who matches before you save." />
      <div className="de-panel">
        <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div className="de-panel-title" style={{ margin: 0 }}>
            Segments
          </div>
          <button type="button" className="de-btn de-btn--primary" onClick={() => setCreating(true)}>
            New segment
          </button>
        </div>
        {!rows ? (
          <Loader />
        ) : rows.length ? (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Last count</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="de-td-strong">{row.name || '—'}</td>
                      <td>{row.last_count ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState icon="👥" title="No segments yet" body="Add an audience, then preview how many customers match." />
        )}
      </div>
      {creating ? (
        <SegmentForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
